// @ts-check
// Editor das variáveis de um modelo do WhatsApp nos envios em massa (contrato 09 de campanhas de WhatsApp, seção 2;
// spec/whatsapp.md 6): para cada variável, "Preencher com" (catálogo TEMPLATE_VARIABLE_SOURCES), o texto fixo ou a
// expressão personalizada e o valor reserva. Guarda no formato de `variable_mappings`; quem usa (campanha da 2B, nó do
// fluxo da 2D) valida com validateVariableMappings e mostra o que falta pelo `missing`.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import {
  TEMPLATE_VARIABLE_SOURCES, VARIABLE_MAPPING_LIMITS, variableSourceOf,
} from '../../../../shared/whatsapp/template-variables.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/whatsapp/template-variables.js').VariableMappings} VariableMappings
 * @typedef {import('../../../../shared/whatsapp/template-variables.js').VariableMapping} VariableMapping
 */

/**
 * Linhas do editor: cabeçalho primeiro, depois o corpo, na ordem do modelo.
 * @param {{ bodyVariables?: string[], headerVariables?: string[] } | null | undefined} template
 * @returns {Array<{ part: 'header' | 'body', name: string, key: string }>}
 */
export function variableRows(template) {
  return [
    ...(template?.headerVariables ?? []).map((name) => ({ part: /** @type {const} */ ('header'), name, key: `header.${name}` })),
    ...(template?.bodyVariables ?? []).map((name) => ({ part: /** @type {const} */ ('body'), name, key: `body.${name}` })),
  ];
}

/**
 * Mapeamento com uma variável trocada, sem mexer nas outras.
 * @param {VariableMappings | null | undefined} mappings @param {'header' | 'body'} part @param {string} name @param {VariableMapping} mapping
 * @returns {VariableMappings}
 */
export function withVariableMapping(mappings, part, name, mapping) {
  const current = { body: { ...(mappings?.body ?? {}) }, header: { ...(mappings?.header ?? {}) } };
  const fallback = String(mapping.fallback ?? '');
  current[part][name] = { expression: String(mapping.expression ?? ''), ...(fallback ? { fallback } : {}) };
  return current;
}

/**
 * @param {{ template: { bodyVariables?: string[], headerVariables?: string[] } | null, mappings: VariableMappings | null, onChange: (mappings: VariableMappings) => void,
 *   missing?: string[], disabled?: boolean, idPrefix?: string }} props
 *   missing: chaves `body.<nome>` e `header.<nome>` sem preenchimento (de validateVariableMappings), mostradas na linha
 */
export function VariableMappingEditor({ template, mappings, onChange, missing = [], disabled = false, idPrefix = 'wa-variable' }) {
  // escolha feita na tela antes de digitar (texto fixo ou expressão ainda vazios)
  const [chosenSources, setChosenSources] = useState(/** @type {Record<string, string>} */ ({}));
  const rows = variableRows(template);
  if (rows.length === 0) return html`<p class="wa-preview-empty">${t('whatsappCampaigns.variables.none')}</p>`;

  return html`<ul class="wa-variables" aria-label=${t('whatsappCampaigns.variables.title')}>
    ${rows.map(({ part, name, key }) => {
      const mapping = mappings?.[part]?.[name] ?? { expression: '' };
      const expression = String(mapping.expression ?? '');
      const source = chosenSources[key] ?? (expression ? variableSourceOf(expression) : '');
      const isMissing = missing.includes(key);
      const fieldId = `${idPrefix}-${part}-${name}`;
      const update = (/** @type {Partial<VariableMapping>} */ change) => onChange(withVariableMapping(mappings, part, name, { ...mapping, ...change }));
      const chooseSource = (/** @type {string} */ value) => {
        setChosenSources({ ...chosenSources, [key]: value });
        const fixed = TEMPLATE_VARIABLE_SOURCES.find((item) => item.key === value && item.expression);
        if (fixed?.expression) update({ expression: fixed.expression });
        else if (value === 'text' && variableSourceOf(expression) !== 'text') update({ expression: '' });
      };
      return html`<li key=${key} class="wa-variable-row" data-missing=${isMissing ? 'true' : undefined}>
        <span class="wa-variable-token" title=${part === 'header' ? t('whatsappCampaigns.variables.headerVariable') : undefined}>{{${name}}}</span>
        <div class="wa-variable-fill">
          <label class="form-field">
            <span class="form-field-label">${t('whatsappCampaigns.variables.fillWith')}</span>
            <select id=${`${fieldId}-source`} class="text-input integration-select" value=${source} disabled=${disabled}
              onChange=${(/** @type {Event} */ event) => chooseSource(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
              <option value="" disabled selected=${source === ''}>${t('whatsappCampaigns.variables.choose')}</option>
              ${TEMPLATE_VARIABLE_SOURCES.map((item) => html`<option key=${item.key} value=${item.key} selected=${item.key === source}>
                ${t(`whatsappCampaigns.variables.sources.${item.key}`)}
              </option>`)}
            </select>
          </label>
          ${source === 'text' || source === 'custom'
            ? html`<label class="form-field">
                <span class="form-field-label">${source === 'text' ? t('whatsappCampaigns.variables.fixedText') : t('whatsappCampaigns.variables.customExpression')}</span>
                <input class="text-input" value=${expression} maxLength=${VARIABLE_MAPPING_LIMITS.maximumExpressionLength} disabled=${disabled}
                  placeholder=${source === 'custom' ? '{{contact.name.firstName}}' : ''}
                  onInput=${(/** @type {Event} */ event) => update({ expression: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
              </label>`
            : null}
        </div>
        ${source !== 'text'
          ? html`<label class="form-field">
              <span class="form-field-label">${t('whatsappCampaigns.variables.fallback')}</span>
              <input class="text-input" value=${mapping.fallback ?? ''} maxLength=${VARIABLE_MAPPING_LIMITS.maximumFallbackLength} disabled=${disabled}
                placeholder=${t('whatsappCampaigns.variables.fallbackPlaceholder')}
                onInput=${(/** @type {Event} */ event) => update({ fallback: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
            </label>`
          : html`<span></span>`}
        ${isMissing
          ? html`<p class="wa-field-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${t('whatsappCampaigns.variables.missing', { variable: name })}</span></p>`
          : null}
      </li>`;
    })}
  </ul>`;
}
